import type { ForemanEmployeeLabor } from '@ups/xplat/api/dto';
import { getPersistableInOutsSignature } from './foreman-employee-in-out-identity.utils';

/**
 * Mark a labor as an unsubmitted device draft at an absolute instant.
 *
 * `Date.now()` is Unix epoch milliseconds, not a local clock-face value, so a
 * user changing device timezone does not alter the instant used for conflict
 * resolution.
 */
export function markForemanLaborDraftChanged(
  labor: ForemanEmployeeLabor,
  changedAtUtcMs: number = Date.now()
): void {
  labor.isChanged = true;
  labor.localChangedAtUtcMs = changedAtUtcMs;
}

/**
 * Record the labor's current values as the committed (server) state. Called
 * wherever a row is known to match the API — the job-list normalize pass and
 * the remote overlay in {@link reconcileLaborsFromRemoteJob}.
 */
export function captureForemanLaborCommittedState(
  labor: ForemanEmployeeLabor
): void {
  labor.committedSignature = getForemanLaborValuesSignature(labor);
}

/**
 * True when the labor is flagged dirty AND its values still differ from the
 * committed state. Editing hours back to the saved value leaves `isChanged`
 * set (the flag is one-way so the mutation points stay cheap), so the draft
 * warning compares content instead of trusting the flag (#30753).
 *
 * A row with no recorded committed state never reached the API: a work-order
 * row the device created for this entry, or a draft cached before the
 * fingerprint existed. There is no baseline to diff, so the question becomes
 * whether the row currently carries anything worth submitting. Entering hours
 * and clearing them again leaves an empty device row that the server has never
 * heard of, and warning about it tells the user to submit nothing.
 */
export function hasForemanLaborDraftEdits(
  labor: ForemanEmployeeLabor | null | undefined
): boolean {
  if (!labor || labor.isChanged !== true) {
    return false;
  }
  if (typeof labor.committedSignature !== 'string') {
    return hasSubmittableForemanLaborContent(labor);
  }
  return labor.committedSignature !== getForemanLaborValuesSignature(labor);
}

/**
 * True when the labor holds something the submit API would persist: hours, an
 * absence, foreman or exception notes, or a persistable time segment.
 *
 * Deliberately ignores `bActive`. A row whose baseline is known compares by
 * signature instead, so this is only asked of rows the server has never seen,
 * where deactivating an empty row changes nothing it could store.
 */
export function hasSubmittableForemanLaborContent(
  labor: ForemanEmployeeLabor
): boolean {
  return (
    labor.isAbsent === true ||
    Number(labor.hours || 0) > 0 ||
    text(labor.foremanNotes) !== '' ||
    text(labor.foremanExceptionNotes) !== '' ||
    getPersistableInOutsSignature(labor.inOuts) !== ''
  );
}

/**
 * Fingerprint of every labor value the device can edit and submit. Compared as
 * a string so the committed state persists in the offline job cache without a
 * second copy of the row.
 */
export function getForemanLaborValuesSignature(
  labor: ForemanEmployeeLabor
): string {
  return [
    // Row identity: copy-forward flows clone a labor by spread, so the clone
    // inherits the source's signature. A new id makes it a draft, as it is.
    text(labor.foremanEmployeeLaborID),
    Number(labor.hours || 0),
    labor.bActive === true,
    labor.isAbsent === true,
    text(labor.absentReasonID),
    text(labor.absentReasonOther),
    text(labor.foremanNotes),
    labor.isException === true,
    text(labor.foremanExceptionNotes),
    text(labor.foremanPhaseID),
    text(labor.foremanEarnCode).toUpperCase(),
    Number(labor.shift || 0),
    getPersistableInOutsSignature(labor.inOuts),
  ].join('|');
}

function text(value: string | null | undefined): string {
  return String(value ?? '').trim();
}
